/* eslint-disable no-console */
import { context as otelContext, type Attributes } from '@opentelemetry/api'
import { logs, SeverityNumber, type LoggerOptions } from '@opentelemetry/api-logs'
import {
  toExceptionAttributes,
  unwrapWrappedLoggerErrorAttributes,
} from '@aient/otel/exception-attributes'
import { OTEL_SERVICE_NAME } from '@/lib/env'
import {
  redactLoggerPayload,
  redactSensitiveHeaders,
} from '@/lib/x402/redaction'

export type LoggerAttributes = Attributes

const CONSOLE_MIRROR_PREFIX = '@aient/logger:'

/** (message, attrs?) | (message, error?, attrs?) — all levels support both. */
export type LevelLogFn = {
  (message: string, attributes?: LoggerAttributes): void
  (message: string, error: Error | unknown, attributes?: LoggerAttributes): void
}

export type ExceptionLogFn = (
  message: string,
  error: unknown,
  attributes?: LoggerAttributes,
) => void

export interface LoggerFacade {
  debug: LevelLogFn
  info: LevelLogFn
  warn: LevelLogFn
  error: LevelLogFn
  fatal: LevelLogFn
  exception: ExceptionLogFn
}

function isAttributes(v: unknown): v is LoggerAttributes {
  return typeof v === 'object' && v !== null && !isDirectErrorInput(v)
}

function isDirectErrorInput(v: unknown): v is Error | Record<string, unknown> {
  return v instanceof Error || (typeof v === 'object' && v !== null && 'stack' in v)
}

function normalizeLogArgs(
  errorOrAttrs?: Error | unknown | LoggerAttributes,
  attributes?: LoggerAttributes,
): { error?: Error | unknown; attributes: LoggerAttributes } {
  if (attributes !== undefined) {
    return {
      error: errorOrAttrs == null ? undefined : errorOrAttrs,
      attributes,
    }
  }

  const wrapped = unwrapWrappedLoggerErrorAttributes(errorOrAttrs)
  if (wrapped) {
    return {
      error: wrapped.error,
      attributes: wrapped.attributes as LoggerAttributes,
    }
  }

  if (isAttributes(errorOrAttrs)) {
    return { attributes: errorOrAttrs }
  }

  if (isDirectErrorInput(errorOrAttrs)) {
    return { error: errorOrAttrs, attributes: {} }
  }

  return { attributes: {} }
}

const safeStringify = (obj: unknown): string => {
  try {
    return JSON.stringify(obj)
  } catch {
    return '[unserializable]'
  }
}

/**
 * Factory for module-scoped loggers.
 * - Matches OpenTelemetry Logger API signature: (name, version?, options?)
 * - Prefixes the name with the service name from OTEL_SERVICE_NAME.
 */
export function getLogger(name: string, version?: string, options?: LoggerOptions): LoggerFacade {
  const fullName = `${OTEL_SERVICE_NAME}.${name}`
  const otelLogger = logs.getLogger(fullName, version, options)

  const emit = (severityNumber: SeverityNumber, severityText: string, body: string, attributes: Attributes = {}) => {
    try {
      // Phase 1.1 x402 redaction stack (plan §5.3, Sec C1): scrub
      // Bearer tokens, x402 payment payloads, and Cookie headers from
      // both the body string AND each string attribute value before
      // they hit OTel collectors / Sentry / any downstream sink.
      const safeBody = redactSensitiveHeaders(body)
      const safeAttributes = redactLoggerPayload(attributes as Record<string, unknown>) as Attributes
      otelLogger.emit({ severityNumber, severityText, body: safeBody, attributes: safeAttributes, context: otelContext.active() })
    } catch {
      // OTel emit failed - already logged to console, nothing more to do
    }
  }

  const timestamp = () => new Date().toISOString()

  const makeLevel = (
    severityNumber: SeverityNumber,
    severityText: string,
    prefix: string,
    consoleFn: (msg: string, rest?: string) => void
  ): LevelLogFn =>
    function level(message: string, errorOrAttrs?: Error | unknown | LoggerAttributes, attributes?: LoggerAttributes) {
      try {
        const { error, attributes: attrs } = normalizeLogArgs(errorOrAttrs, attributes)

        // Mirror the same redaction at the console-mirror layer so a
        // dev's terminal output cannot leak a token either (Sec C1
        // documented "screen-share risk").
        const safeMessage = redactSensitiveHeaders(message)

        if (error !== undefined) {
          const errorAttributes = toExceptionAttributes(error)
          const merged = { ...attrs, ...errorAttributes }
          consoleFn(`${CONSOLE_MIRROR_PREFIX} [${prefix}] [${timestamp()}] [2 lines] ${safeMessage}`, redactSensitiveHeaders(safeStringify(attrs)))
          consoleFn(`${CONSOLE_MIRROR_PREFIX} ${redactSensitiveHeaders(safeStringify(errorAttributes))}`)
          emit(severityNumber, severityText, message, merged)
        } else {
          consoleFn(`${CONSOLE_MIRROR_PREFIX} [${prefix}] [${timestamp()}] ${safeMessage}`, redactSensitiveHeaders(safeStringify(attrs)))
          emit(severityNumber, severityText, message, attrs)
        }
      } catch {
        consoleFn(`${CONSOLE_MIRROR_PREFIX} [${prefix}] [logger-error]`, redactSensitiveHeaders(message))
      }
    }

  const error = makeLevel(SeverityNumber.ERROR, 'ERROR', 'E', console.error.bind(console))

  return {
    debug: makeLevel(SeverityNumber.DEBUG, 'DEBUG', 'D', console.debug.bind(console)),
    info: makeLevel(SeverityNumber.INFO, 'INFO', 'I', console.info.bind(console)),
    warn: makeLevel(SeverityNumber.WARN, 'WARN', 'W', console.warn.bind(console)),
    error,
    fatal: makeLevel(SeverityNumber.FATAL, 'FATAL', 'F', console.error.bind(console)),
    // Supplying an explicit attributes object removes the two-argument
    // ambiguity between plain-object provider failures and log metadata.
    exception: (message, exception, attributes) => error(message, exception, attributes ?? {}),
  }
}

/**
 * Optional: console-like wrapper bound to a named logger.
 * Usage: const consoleLike = getOtelConsole('my/module')
 */
export function getOtelConsole(name: string, version?: string, options?: LoggerOptions) {
  const l = getLogger(name, version, options)
  return {
    log: l.info,
    info: l.info,
    debug: l.debug,
    warn: l.warn,
    error: (message: string | Error, ...args: unknown[]) => {
      if (message instanceof Error) {
        l.error(message.message, message, args[0] as LoggerAttributes | undefined)
      } else {
        l.error(message, args[0] as Error | unknown, args[1] as LoggerAttributes | undefined)
      }
    },
  }
}
